import { DEBOUNCE } from '@/core/core.constants';
import { useDebounce } from '@/core/hooks/useDebounce.hook';
import { doTrack } from '@/track/track.service';
import { Button, TextField } from '@seeqdev/qomponents';
import type { GridApi, ProcessCellForExportParams } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';

const processCellForExcelExport = (params: ProcessCellForExportParams) => {
  const { value, column, parseValue, formatValue } = params;
  // If the column is a numeric column, return the value as is to let Excel handle the formatting
  return column.getColDef().type === 'numericColumn' ? value : formatValue(parseValue(value)) ?? value;
};

interface EvidenceTableContainerProps {
  children: React.ReactNode;
  quickFilter: string;
  onQuickFilterChange: (value: string) => void;
  gridRef: React.MutableRefObject<AgGridReact<any> | null>;
  gridApi: GridApi | null;
  roomName: string;
  viewName: string;
}

export const EvidenceTableContainer: React.FunctionComponent<EvidenceTableContainerProps> = ({
  children,
  quickFilter,
  onQuickFilterChange,
  gridApi,
  gridRef,
  roomName,
  viewName,
}) => {
  const { t } = useTranslation();

  const setGridFilter = useDebounce((inputValue: string) => {
    gridRef.current?.api.setGridOption('quickFilterText', inputValue);
  }, DEBOUNCE.MEDIUM);

  return (
    <div id="container" className="tw:h-full flexFill flexRowContainer positionRelative evidence-panel">
      <div className="table-filter flexColumnContainer">
        <TextField
          type="text"
          id="table-filter"
          inputWidth={200}
          extraClassNames="mr5 height-24 mt7"
          placeholder={t('VANTAGE.EVIDENCE_TABLE.FILTERS.PLACEHOLDER')}
          value={quickFilter}
          onChange={(e) => {
            const inputValue = (e.target as HTMLInputElement).value;
            onQuickFilterChange(inputValue);
            setGridFilter(inputValue);
          }}
          testId="table-filter-input"
        />
        <Button
          onClick={() => {
            onQuickFilterChange('');
            gridApi?.setGridOption('quickFilterText', '');
            gridApi?.setFilterModel(null);
          }}
          label=""
          extraClassNames="mt10"
          size="lg"
          variant="no-border"
          type="button"
          icon="fa-circle-xmark"
          iconColor="grey"
          iconStyle="color"
          tooltip={t('VANTAGE.EVIDENCE_TABLE.FILTERS.CLEAR')}
          testId="clear-table-filters"
        />
        <Button
          onClick={() => {
            doTrack('Vantage', 'Export Table To Excel', {
              room: roomName,
              view: viewName,
              row_count: gridApi?.getDisplayedRowCount() || 0,
              column_count: gridApi?.getColumnDefs()?.length || 0,
            });
            gridApi?.exportDataAsExcel({
              fileName: `${roomName} - ${viewName}.xlsx`,
              processCellCallback: processCellForExcelExport,
            });
          }}
          label=""
          extraClassNames="mt12 pl0 ml0 fc-icon-large"
          size="lg"
          variant="no-border"
          type="button"
          icon="fc-excel"
          tooltip={t('EXPORT_EXCEL.HEADER')}
          testId="export-to-excel-button"
        />
      </div>
      {children}
    </div>
  );
};
